/**
 * Iter 67cn — Vendor dashboard tabs list, extracted from VendorDashboard.jsx.
 *
 * Desktop (≥sm): the original 11-tab gradient-pill list inline.
 *
 * Iter 67eh — Mobile (<sm): a 3-column grid of colored icon tiles
 * mirroring the AdminOpsPanel mobile menu — much friendlier touch
 * targets than the cramped horizontally-scrolling pills. Each tile
 * inherits its tab's accent color so vendors learn the mapping (Cars =
 * sky, Bookings = blue, Payouts = emerald, …) and recognise sections
 * instantly. Badges (upcoming, pending change requests, access requests)
 * float in the tile's top-right corner.
 *
 * Tailwind JIT cannot resolve interpolated class names, so each tab's full
 * className string is stored as a literal in `TAB_DEFS` — verbose but it
 * keeps the build correct and the colours pixel-identical to the original.
 */
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Car as CarIcon, BookOpen, DollarSign, Wallet, Users,
  Activity, Building2, CalendarRange, Receipt, Globe2,
  Calendar as CalendarIcon, FileText as FileTextIcon, LineChart, BarChart3,
} from "lucide-react";

const TAB_DEFS = [
  {
    value: "cars",
    label: "My cars",
    Icon: CarIcon,
    // Iter 67eh — per-tile colour palette for the mobile grid.
    mobileBg: "bg-sky-50",
    mobileFg: "text-sky-700",
    mobileRing: "ring-sky-200",
    mobileActive: "from-sky-500 to-blue-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-sky-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-sky-200 hover:text-sky-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center ring-1 ring-sky-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "calendar",
    label: "Calendar",
    Icon: CalendarIcon,
    mobileBg: "bg-indigo-50",
    mobileFg: "text-indigo-700",
    mobileRing: "ring-indigo-200",
    mobileActive: "from-indigo-500 to-indigo-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-indigo-200 hover:text-indigo-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center ring-1 ring-indigo-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "bookings",
    label: "Bookings",
    Icon: BookOpen,
    mobileBg: "bg-blue-50",
    mobileFg: "text-blue-700",
    mobileRing: "ring-blue-200",
    mobileActive: "from-blue-500 to-blue-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-200 hover:text-blue-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center ring-1 ring-blue-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "payouts",
    label: "Payouts",
    Icon: DollarSign,
    mobileBg: "bg-emerald-50",
    mobileFg: "text-emerald-700",
    mobileRing: "ring-emerald-200",
    mobileActive: "from-emerald-500 to-emerald-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-emerald-200 hover:text-emerald-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "billing",
    label: "Billing",
    Icon: Wallet,
    mobileBg: "bg-violet-50",
    mobileFg: "text-violet-700",
    mobileRing: "ring-violet-200",
    mobileActive: "from-violet-500 to-purple-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-violet-200 hover:text-violet-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center ring-1 ring-violet-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "fleet-finance",
    label: "Fleet finance",
    Icon: LineChart,
    mobileBg: "bg-teal-50",
    mobileFg: "text-teal-700",
    mobileRing: "ring-teal-200",
    mobileActive: "from-teal-500 to-cyan-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-teal-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-teal-200 hover:text-teal-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center ring-1 ring-teal-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "reports",
    label: "Reports",
    Icon: BarChart3,
    mobileBg: "bg-sky-50",
    mobileFg: "text-sky-700",
    mobileRing: "ring-sky-200",
    mobileActive: "from-sky-500 to-blue-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-sky-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-sky-200 hover:text-sky-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center ring-1 ring-sky-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "fiscalization",
    label: "Fiscalization",
    Icon: Receipt,
    mobileBg: "bg-red-50",
    mobileFg: "text-red-700",
    mobileRing: "ring-red-200",
    mobileActive: "from-red-500 to-orange-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-red-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-red-200 hover:text-red-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-red-100 text-red-600 flex items-center justify-center ring-1 ring-red-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "widget",
    label: "Booking widget",
    Icon: Globe2,
    mobileBg: "bg-violet-50",
    mobileFg: "text-violet-700",
    mobileRing: "ring-violet-200",
    mobileActive: "from-indigo-500 to-violet-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-indigo-200 hover:text-indigo-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center ring-1 ring-indigo-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "rates",
    label: "Rates",
    Icon: CalendarRange,
    mobileBg: "bg-fuchsia-50",
    mobileFg: "text-fuchsia-700",
    mobileRing: "ring-fuchsia-200",
    mobileActive: "from-violet-500 to-fuchsia-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-500 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-violet-200 hover:text-violet-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center ring-1 ring-violet-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "staff",
    label: "Staff",
    Icon: Users,
    mobileBg: "bg-amber-50",
    mobileFg: "text-amber-700",
    mobileRing: "ring-amber-200",
    mobileActive: "from-amber-500 to-orange-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-amber-200 hover:text-amber-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center ring-1 ring-amber-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "activity",
    label: "Activity log",
    Icon: Activity,
    mobileBg: "bg-purple-50",
    mobileFg: "text-purple-700",
    mobileRing: "ring-purple-200",
    mobileActive: "from-indigo-500 to-purple-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-indigo-200 hover:text-indigo-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center ring-1 ring-indigo-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "documents",
    label: "Documents",
    Icon: FileTextIcon,
    mobileBg: "bg-slate-100",
    mobileFg: "text-slate-700",
    mobileRing: "ring-slate-200",
    mobileActive: "from-slate-700 to-slate-900",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-slate-700 data-[state=active]:to-slate-900 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-slate-300 hover:text-slate-800 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center ring-1 ring-slate-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
  {
    value: "profile",
    label: "Business profile",
    Icon: Building2,
    mobileBg: "bg-rose-50",
    mobileFg: "text-rose-700",
    mobileRing: "ring-rose-200",
    mobileActive: "from-rose-500 to-pink-600",
    triggerClass: "group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-rose-200 hover:text-rose-700 transition-all",
    iconWrapClass: "h-7 w-7 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center ring-1 ring-rose-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors",
  },
];

function _Badge({ count, color, testId, ariaLabel, title }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full ${color} text-white text-[10px] font-bold leading-none ring-1 ring-white/40`}
      data-testid={testId}
      aria-label={ariaLabel}
      title={title}
    >
      {count}
    </span>
  );
}

export default function VendorTabsList({
  upcomingCount = 0,
  pendingChangeCount = 0,
  pendingAccessCount = 0,
  activeTab = "cars",
  onTabChange = () => {},
}) {
  return (
    <>
      {/* Iter 67eh — mobile menu (<sm): bigger touch-friendly colored icon tiles,
          mirroring the AdminOpsPanel pattern. Hidden above sm where the
          original gradient-pill list takes over. */}
      <_MobileVendorMenu
        activeTab={activeTab}
        onTabChange={onTabChange}
        upcomingCount={upcomingCount}
        pendingChangeCount={pendingChangeCount}
        pendingAccessCount={pendingAccessCount}
      />
      <TabsList
        className="hidden sm:flex w-full justify-start flex-wrap gap-2 h-auto bg-gradient-to-r from-emerald-50/70 via-teal-50/40 to-sky-50/60 border border-emerald-100 p-2 rounded-2xl shadow-sm ring-1 ring-emerald-50"
        data-testid="vendor-main-tabs"
      >
        {TAB_DEFS.map((def) => {
          const { value, label, Icon, triggerClass, iconWrapClass } = def;
          return (
            <TabsTrigger key={value} value={value} className={triggerClass} data-testid={`tab-${value}`}>
              <span className={iconWrapClass}>
                <Icon className="h-4 w-4" />
              </span>
              {label}
              {value === "calendar" && (
                <_Badge
                  count={upcomingCount}
                  color="bg-amber-500"
                  testId="tab-calendar-badge"
                  ariaLabel={`${upcomingCount} upcoming bookings in next 7 days`}
                />
              )}
              {value === "bookings" && (
                <_Badge
                  count={pendingChangeCount}
                  color="bg-rose-500"
                  testId="tab-bookings-change-badge"
                  ariaLabel={`${pendingChangeCount} pending customer change requests`}
                  title={`${pendingChangeCount} pending change request${pendingChangeCount === 1 ? "" : "s"} from customers`}
                />
              )}
              {value === "profile" && (
                <_Badge
                  count={pendingAccessCount}
                  color="bg-blue-600"
                  testId="tab-profile-access-badge"
                  ariaLabel={`${pendingAccessCount} pending admin access requests`}
                  title={`${pendingAccessCount} admin access request${pendingAccessCount === 1 ? "" : "s"} awaiting your decision`}
                />
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </>
  );
}

// Iter 67eh — Mobile vendor menu: 3-column grid of colored icon tiles.
// Active tile uses a vivid gradient (per-tab palette) + white text;
// idle tiles render their pastel background with the tab's accent color.
// Badges float in the top-right corner so they survive the bigger
// touch-target layout.
function _MobileVendorMenu({
  activeTab,
  onTabChange,
  upcomingCount,
  pendingChangeCount,
  pendingAccessCount,
}) {
  return (
    <div
      className="sm:hidden grid grid-cols-3 gap-2.5 mb-4"
      role="tablist"
      data-testid="vendor-mobile-menu"
    >
      {TAB_DEFS.map((def) => {
        const { value, label, Icon, mobileBg, mobileFg, mobileRing, mobileActive } = def;
        const isActive = activeTab === value;
        const badge =
          value === "calendar" ? upcomingCount :
          value === "bookings" ? pendingChangeCount :
          value === "profile"  ? pendingAccessCount : 0;
        const badgeColor =
          value === "calendar" ? "bg-amber-500" :
          value === "bookings" ? "bg-rose-500"  :
          value === "profile"  ? "bg-blue-600"  : "bg-neutral-500";
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(value)}
            className={`relative rounded-2xl px-2 py-3 ring-1 transition flex flex-col items-center gap-1.5 ${
              isActive
                ? `bg-gradient-to-br ${mobileActive} text-white ring-white/20 shadow-lg`
                : `${mobileBg} ${mobileFg} ${mobileRing} hover:shadow-md`
            }`}
            data-testid={`vendor-mobile-tile-${value}`}
          >
            <span
              className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                isActive ? "bg-white/15" : "bg-white/70 backdrop-blur"
              }`}
            >
              <Icon className="h-4 w-4" />
            </span>
            <span className={`text-[11px] font-bold uppercase tracking-wider truncate w-full text-center ${
              isActive ? "text-white" : ""
            }`}>
              {label}
            </span>
            {badge > 0 && (
              <span
                className={`absolute top-1 right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full ${badgeColor} text-white text-[10px] font-bold leading-none ring-2 ring-white`}
                aria-label={`${badge} notification${badge === 1 ? "" : "s"}`}
                data-testid={`vendor-mobile-badge-${value}`}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
